import { SegmentRequestTypes, SegmentTypes } from './types';
import { ActionType, createAction } from '@fullstory/redux-util/utils/types';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import * as searchSettings from '@fullstory/proto/fs/services/search-settings';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

const cacheKeysToInvalidate = (orgId: string) => [
  defaultCacheKey(dxo.SegmentsPublic.GetAllSegmentsEndpoint, {
    includeArchived: true,
    orgId,
  }),
  defaultCacheKey(dxo.SegmentsPublic.GetInitialSegmentsEndpoint, {
    includeArchived: false,
    orgId,
  }),
];

export const SegmentActions = {
  setLastKnownSegment: (segmentId: string) =>
    createAction({
      type: SegmentTypes.SetLastKnownSegment,
      segmentId,
    }),
};

export const SegmentRequestActions = {
  getSegment: (orgId: string, segmentId: string, force: boolean = false) =>
    createGRPCAction<typeof dxo.SegmentsPublic.GetSegment>()(
      {
        type: SegmentRequestTypes.GetSegmentRequest,
        grpcFunction: dxo.SegmentsPublic.GetSegment,
        body: { orgId, segmentId },
        force,
        readWrite: 'read',
      },
      SegmentRequestTypes.GetSegmentSuccess,
      SegmentRequestTypes.GetSegmentFailure,
    ),
  getAllSegments: (orgId: string, force: boolean = false) =>
    createGRPCAction<typeof dxo.SegmentsPublic.GetAllSegments>()(
      {
        type: SegmentRequestTypes.GetAllSegmentsRequest,
        grpcFunction: dxo.SegmentsPublic.GetAllSegments,
        body: { orgId, includeArchived: true },
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
          },
        },
        force,
        readWrite: 'read',
      },
      SegmentRequestTypes.GetAllSegmentsSuccess,
      SegmentRequestTypes.GetAllSegmentsFailure,
    ),
  getInitialSegments: (orgId: string, force: boolean = false) =>
    createGRPCAction<typeof dxo.SegmentsPublic.GetInitialSegments>()(
      {
        type: SegmentRequestTypes.GetInitialSegmentsRequest,
        grpcFunction: dxo.SegmentsPublic.GetInitialSegments,
        body: { orgId },
        force,
        readWrite: 'read',
      },
      SegmentRequestTypes.GetInitialSegmentsSuccess,
      SegmentRequestTypes.GetInitialSegmentsFailure,
    ),
  setSegment: (orgId: string, segment: searchSettings.Segment, visibility: Visibility) =>
    createGRPCAction<typeof dxo.SegmentsPublic.SetSegment>()(
      {
        type: SegmentRequestTypes.SetSegmentRequest,
        grpcFunction: dxo.SegmentsPublic.SetSegment,
        body: { orgId, segment, visibility },
        force: true,
        readWrite: 'write',
        invalidateCacheKey: cacheKeysToInvalidate(orgId),
      },
      SegmentRequestTypes.SetSegmentSuccess,
      SegmentRequestTypes.SetSegmentFailure,
    ),
  archiveSegment: (orgId: string, segmentId: string, archived: boolean) =>
    createGRPCAction<typeof dxo.SegmentsPublic.ArchiveSegment>()(
      {
        type: SegmentRequestTypes.ArchiveSegmentRequest,
        grpcFunction: dxo.SegmentsPublic.ArchiveSegment,
        body: { orgId, segmentId, archived },
        force: true,
        readWrite: 'write',
        invalidateCacheKey: cacheKeysToInvalidate(orgId),
      },
      SegmentRequestTypes.ArchiveSegmentSuccess,
      SegmentRequestTypes.ArchiveSegmentFailure,
    ),
  starSegment: (orgId: string, segmentId: string, starred: boolean) =>
    createGRPCAction<typeof dxo.SegmentsPublic.StarSegment>()(
      {
        type: SegmentRequestTypes.StarSegmentRequest,
        grpcFunction: dxo.SegmentsPublic.StarSegment,
        body: { orgId, segmentId, starred },
        force: true,
        readWrite: 'write',
        invalidateCacheKey: cacheKeysToInvalidate(orgId),
      },
      SegmentRequestTypes.StarSegmentSuccess,
      SegmentRequestTypes.StarSegmentFailure,
    ),
  followSegment: (orgId: string, segmentId: string, followed: boolean) =>
    createGRPCAction<typeof dxo.SegmentsPublic.FollowSegment>()(
      {
        type: SegmentRequestTypes.FollowSegmentRequest,
        grpcFunction: dxo.SegmentsPublic.FollowSegment,
        body: { orgId, segmentId, followed },
        force: true,
        readWrite: 'write',
      },
      SegmentRequestTypes.FollowSegmentSuccess,
      SegmentRequestTypes.FollowSegmentFailure,
    ),
  markSegmentViewed: (orgId: string, segmentId: string) =>
    createGRPCAction<typeof dxo.SegmentsPublic.MarkSegmentViewed>()(
      {
        type: SegmentRequestTypes.MarkSegmentViewedRequest,
        grpcFunction: dxo.SegmentsPublic.MarkSegmentViewed,
        body: { orgId, segmentId },
        force: true,
        readWrite: 'write',
      },
      SegmentRequestTypes.MarkSegmentViewedSuccess,
      SegmentRequestTypes.MarkSegmentViewedFailure,
    ),
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof dxo.SegmentsPublic.GetSegmentsAccess>()(
      {
        type: SegmentRequestTypes.SegmentAccessRequest,
        grpcFunction: dxo.SegmentsPublic.GetSegmentsAccess,
        body,
        readWrite: 'read',
      },
      SegmentRequestTypes.SegmentAccessSuccess,
      SegmentRequestTypes.SegmentAccessFailure,
    ),
};

export type SegmentActions =
  | ActionType<typeof SegmentActions>
  | RequestActionType<typeof SegmentRequestActions>;
